"use client";

import { Core } from "@faire/design-tokens";
import { Typography, TypographyStyles } from "@faire/web/slate/Typography";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import * as React from "react";
import styled, {
  css,
  useTheme,
  DefaultTheme as StyledComponentsTheme,
} from "styled-components";

import { ColorOrKey } from "../Color";
import { Checkmark as CheckmarkIcon } from "../icons/deprecated/Checkmark";
import { IntermediateCheckmark } from "../icons/IntermediateCheckmark";
import { SVGIcon } from "../icons/SVGIcon";
import { Row } from "../Layout";
import { ICheckbox } from "../Theme/Checkbox";
import {
  highVisSlateComponent,
  HIGH_VIS_FILTER,
} from "../utils/highVisSlateComponent";

import { getCheckboxColors } from "./utils";

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/checkbox)
 */
export const Checkbox: React.FC<Checkbox.IProps> = ({ children, ...props }) => {
  const theme = useTheme();

  const colors = getCheckboxColors(props, theme);

  const {
    align,
    "aria-label": ariaLabel,
    checked: isChecked,
    className,
    "data-test-id": dataTestId,
    disabled,
    iconProps,
    id,
    intermediate: isIntermediate = false,
    label,
    margin,
    name,
    onBlur,
    onChange,
    secondaryLabel,
    size = 16,
    value,
  } = props;

  let ariaChecked: "true" | "false" | "mixed";
  if (isChecked) {
    ariaChecked = "true";
  } else if (isIntermediate) {
    ariaChecked = "mixed";
  } else {
    ariaChecked = "false";
  }

  const labelId = useUniqueId("checkbox-label-");
  const checkboxId = id ?? labelId;

  return (
    <CheckboxWrapper
      $align={align}
      $checked={isChecked || isIntermediate}
      className={className}
      $colors={colors}
      $disabled={disabled}
      $size={size}
      theme={theme}
    >
      <input
        aria-label={ariaLabel}
        aria-checked={ariaChecked}
        checked={isChecked || isIntermediate}
        data-test-id={dataTestId}
        disabled={disabled}
        id={checkboxId}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        type="checkbox"
        value={value}
      />
      <span className="visible-wrapper">
        <Checkmark
          $checked={isChecked || isIntermediate}
          $colors={colors}
          $disabled={disabled}
          $margin={margin}
          role="presentation"
          $size={size}
          theme={theme}
        >
          {isChecked ? (
            <CheckmarkIcon
              aria-hidden={true}
              fontSize={`${size}px`}
              {...(iconProps ?? {})}
            />
          ) : (
            <IntermediateCheckmark
              aria-hidden={true}
              fontSize={`${size}px`}
              {...(iconProps ?? {})}
            />
          )}
        </Checkmark>
        {children || label ? (
          <Label
            $checked={isChecked || isIntermediate}
            $colors={colors}
            $disabled={disabled}
            htmlFor={checkboxId}
            theme={theme}
          >
            <Row>
              {label}
              {/* It's important to wrap the children in a label so that the label connects to the input via id & makes it clickable*/}
              {children}
            </Row>
            <Typography color={Core.text.subdued}>{secondaryLabel}</Typography>
          </Label>
        ) : null}
      </span>
    </CheckboxWrapper>
  );
};

const Checkmark = styled.div<{
  $checked?: boolean;
  $colors: ICheckbox;
  $disabled?: boolean;
  $margin?: string;
  $size: number;
}>`
  align-items: center;
  background-color: ${({ $checked, $colors, $disabled }) =>
    $checked || $disabled ? $colors.fill : "transparent"};
  border: 1px solid ${({ $colors }) => $colors.border};
  box-sizing: border-box;
  color: ${({ $checked, $colors }) =>
    $checked ? $colors.icon : "transparent"};
  cursor: ${({ $disabled }) => ($disabled ? "auto" : "pointer")};
  display: flex;
  justify-content: center;
  margin: ${({ $margin }) => $margin ?? "0"};
  margin-block: 2px; // Margin to center the checkmark since the lineheight is 20px & checkmark height is 16px
  ${({ $size }) => css`
    flex: 0 0 ${$size}px;
    height: ${$size}px;
    line-height: ${$size}px;
    width: ${$size}px;
  `};
`;

const Label = styled.label<{
  $checked?: boolean;
  $colors: ICheckbox;
  $disabled?: boolean;
}>`
  cursor: ${({ $disabled }) => ($disabled ? "auto" : "pointer")};
  padding: 0 0 0 8px !important;
  ${({ $checked, $colors, theme }) =>
    TypographyStyles(
      ($checked
        ? { ...$colors.checked, theme }
        : { ...$colors.base, theme }) ?? {
        theme,
      }
    )}
  text-align: left;
`;

const CheckboxWrapper = styled.div<{
  $align?: "flex-start" | "center" | "flex-end";
  $checked?: boolean;
  $colors: ICheckbox;
  $disabled?: boolean;
  $size: number;
}>`
  ${({ theme }) => TypographyStyles({ theme })};
  align-items: ${({ $align }) => $align ?? "center"};
  display: flex;
  justify-content: flex-start;
  position: relative;

  input {
    position: absolute;
    left: 0;
    ${({ $size }) => css`
      height: ${$size}px;
      width: ${$size}px;
    `};
    &:hover {
      cursor: ${({ $disabled }) => ($disabled ? "auto" : "pointer")};
    }
  }
  // Note for vanilla-extract conversion:  This CSS is a little tricky
  // We want to outline the .visible-wrapper when it's sibling input
  // has focus-visible
  input:focus-visible + .visible-wrapper {
    background-color: Highlight;
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
  }
  .visible-wrapper {
    align-items: ${({ $align }) => $align ?? "center"};
    display: flex;
  }
  &:hover {
    ${Label} {
      ${({ $colors, theme }) =>
        TypographyStyles({ theme, ...$colors.hover } ?? { theme })}
    }
    ${Checkmark} {
      border: 1px solid ${({ $colors }) => $colors.borderActive};
    }
  }
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;

export default Checkbox;

export namespace Checkbox {
  export interface IProps {
    /**
     * align-self property for the checkbox and label.
     * Use `flex-start` to align the checkbox to the top,
     * when the label is multi-line.
     */
    align?: "flex-start" | "flex-end" | "center";
    /** Text read via screenreader.  Do not use this when label or children text is
     *  provided because it overrides the text. It should only be used in the rare
     * case when there is no text in the checkbox (only an icon or image) */
    "aria-label"?: string;
    /** Color of the border of the checkbox. */
    borderColor?: ColorOrKey;
    /** Sets whether the checkbox is checked or unchecked */
    checked: boolean;
    children?: React.ReactNode;
    className?: string;
    /** Color of the checkbox, but not the label text. */
    color?: ColorOrKey;
    "data-test-id"?: string;
    /** Prevents checkbox from being clicked, and invokes the `disabled` styles. */
    disabled?: boolean;
    /** style for the checkmark icon in the checkbox. */
    iconProps?: Partial<SVGIcon.IProps>;
    id?: string;
    /**
     * Whether the checkbox is in an "intermediate" (semi-checked) state.
     * Useful for multi-level checkboxes where some but not all of the child
     * options of a parent option is selected.
     * @default 'false'
     */
    intermediate?: boolean;
    /** The text to the right of the checkbox. */
    label?: string;
    /**
     * Margin around the checkbox, defaults to 8px to the right
     */
    margin?: string;
    /** name for the input */
    name?: string;
    /** onBlur handler for input field. */
    onBlur?: React.HTMLAttributes<HTMLInputElement>["onBlur"];
    /** onChange handler for input field. */
    onChange?: React.HTMLAttributes<HTMLInputElement>["onChange"];
    /**
     * Whether to use the theme's secondary checkbox colors by default.
     */
    secondary?: boolean;
    /** The lighter text to the right of the checkbox. */
    secondaryLabel?: string;
    /** sets the size of the checkbox, but not the label. */
    size?: number;
    theme?: StyledComponentsTheme;
    /** value for input field. */
    value?: string;
  }
}
